import { Game, eModelAsset } from "../../Game";
import { GameWorld } from "../Game World/GameWorld";
import { TIMER, Gameplay, eBatchGroup } from "../Gameplay";
import { Pool } from "../../Utils/Collections/Pool";
import { DeliveryPoint } from "./DeliveryPoint";
import { Character } from "../Player/Character";
import { ParcelManager } from "./ParcelManager";
import { ModelUtils } from "../../Utils/ModelUtils";



export const enum eParcelState {
    /**
     * Balicek je neaktivni
     */
    inactive,
    /**
     * Balicek je pripraven k vyzvednuti ve vydejne
     */
    readyToPickUp,
    /**
     * Probiha vyzvednuti
     */
    pickingUp,
    /**
     * Probiha dorucovani
     */
    delivering,
    /**
     * Balicek je dorucen
     */
    delivered,
    /**
     * Zahozeni balicku, pokud dojde ke smrti postavy a balicek je ve vydejne navic
     */
    discard,
}



export class Parcel {
    /**
     * Vyska balicku (osa Y)
     */
    static readonly HEIGHT = 0.08;

    /**
     * Zakladni entita, ze ktere se klonuji ostatni entity
     */
    private static _1ST_ENTITY: pc.Entity;

    get uid(): number { return this._uid; }
    private _uid: number;

    /**
     * (RO) Entita s balickem
     */
    get entity(): pc.Entity { return this._entity; }
    private _entity: pc.Entity;

    /**
     * (RO) Aktualni stav balicku
     */
    get state(): eParcelState { return this._state; }
    private _state: eParcelState;

    private _time: number;

    private _tmpPos1: pc.Vec3;
    private _tmpPos2: pc.Vec3;

    private static _MATERIALS1: pc.StandardMaterial[];
    private static _MATERIALS2: pc.StandardMaterial[];

    /**
     * Postava hrace, ktera balicek dorucuje
     */
    private _plChar: Character;

    /**
     * Index balicku v seznamu balicku postavy hrace, ktera balicek dorucuje
     */
    private _plCharPkgId: number;

    private static _UPDATE_FNC: (() => void)[];

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(uid: number) {
        this._uid = uid;

        // Aktualizacni funkce
        if (!Parcel._UPDATE_FNC) {
            Parcel._UPDATE_FNC = [];
            Parcel._UPDATE_FNC[eParcelState.pickingUp] = this.updatePickingUp;
            Parcel._UPDATE_FNC[eParcelState.delivering] = this.updateDelivering;
            Parcel._UPDATE_FNC[eParcelState.delivered] = this.updateDelivered;
            Parcel._UPDATE_FNC[eParcelState.discard] = this.updateDiscard;
        }

        // Entita
        if (!Parcel._1ST_ENTITY) {
            Parcel._1ST_ENTITY = new pc.Entity();
            Parcel._1ST_ENTITY.enabled = false;

            Parcel._1ST_ENTITY.addComponent(pc.eCompomentType.model, {
                type: pc.eModelType.asset,
                asset: Game.APP.assets.find(eModelAsset.parcel, pc.eAssetType.model),
                castShadows: false,
                castShadowsLightmap: false,
                batchGroupId: Gameplay.instance.batchGroups[eBatchGroup.dynamic],
            });

            this._entity = Parcel._1ST_ENTITY;

            // Ulozit material
            Parcel._MATERIALS1 = [];
            Parcel._MATERIALS2 = [];
            ModelUtils.cloneModelMaterials(Parcel._1ST_ENTITY.model, {
                blendType: pc.BLEND_NORMAL,
                opacity: 0.1
            }, Parcel._MATERIALS1, Parcel._MATERIALS2, Game.APP.assets, "opacity_");
        }
        else {
            this._entity = Parcel._1ST_ENTITY.clone();
        }

        ModelUtils.setOpacity(this._entity.model, 1);
        Game.APP.root.addChild(this._entity);

        this._tmpPos1 = new pc.Vec3();
        this._tmpPos2 = new pc.Vec3();

        this._state = eParcelState.inactive;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        if (this._state != eParcelState.inactive) {
            // Deaktivovat entitu a vratit ji do poolu
            this._state = eParcelState.inactive;
            this._entity.enabled = false;

            ParcelManager.instance.freeParcels.returnItem(this);
        }
    }

    //-------------------------------------------------------------------------
    /**
     * 
     * @param pos Pripravit balicek k vyzvednuti ve vydejne
     */
    //-------------------------------------------------------------------------
    prepareForPickup(x: number, y: number, z: number): Parcel {
        if (this._state != eParcelState.inactive && this._state != eParcelState.readyToPickUp)
            throw new Error("Invalid parcel's state");

        let batchGroupId = Gameplay.instance.batchGroups[eBatchGroup.dynamic];

        if (this._entity.model.batchGroupId != batchGroupId) {
            this._entity.model.meshInstances.forEach((mesh: pc.MeshInstance, i: number) => {
                mesh.material = Parcel._MATERIALS1[i];
                mesh.setParameter("material_opacity", 1);
            });
            this._entity.model.batchGroupId = batchGroupId;
        }

        this._entity.setPosition(x, y, z);
        this._entity.setEulerAngles(0, 0, 0);
        this._entity.enabled = true;

        this._state = eParcelState.readyToPickUp;

        return this;
    }

    //-------------------------------------------------------------------------
    /**
     * Vyzvednout balicek
     */
    //-------------------------------------------------------------------------
    pickup(plChar: Character, plPkgId: number): Parcel {
        if (this._state != eParcelState.readyToPickUp)
            throw new Error("Invalid parcel's state");

        this._state = eParcelState.pickingUp;
        this._time = TIMER.time;

        this._plChar = plChar;
        this._plCharPkgId = plPkgId;

        // Ulozit pocatecni pozici
        this._tmpPos1.copy(this._entity.getPosition());

        // Aktivovat         
        ParcelManager.instance.addActParcel(this);

        return this;
    }

    //-------------------------------------------------------------------------
    /**
     * Balicek byl dorucen do dodavky
     * @param deliveryPoint
     */
    //-------------------------------------------------------------------------
    deliver(deliveryPoint: DeliveryPoint): Parcel {
        if (this._state != eParcelState.delivering)
            throw new Error("Invalid parcel's state");

        this._state = eParcelState.delivered;
        this._time = TIMER.time;

        this._tmpPos1.copy(this._entity.getPosition());
        this._tmpPos2.set(this._tmpPos1.x, this._tmpPos1.y + 0.25, this._tmpPos1.z + 0.25 * deliveryPoint.dir);

        return this;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    discard(): Parcel {
        if (this._state != eParcelState.readyToPickUp && this._state != eParcelState.delivering)
            throw new Error("Invalid parcel's state");

        if (this._state == eParcelState.readyToPickUp)
            ParcelManager.instance.addActParcel(this);

        this._entity.model.batchGroupId = -1;
        this._entity.model.meshInstances.forEach((mesh: pc.MeshInstance, i: number) => {
            mesh.material = Parcel._MATERIALS2[i];
        });

        this._state = eParcelState.discard;
        this._time = TIMER.time;

        return this;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): boolean {
        let fnc = Parcel._UPDATE_FNC[this._state];

        if (fnc)
            fnc.call(this);

        return (this._state != eParcelState.inactive);
    }

    //-------------------------------------------------------------------------
    /**
     * Zpracovat presun balicku z vydejny k hraci
     */
    //-------------------------------------------------------------------------
    private updatePickingUp(): void {
        let progress = (TIMER.time - this._time) / 250;

        if (progress >= 1) {
            progress = 1;
            this._state = eParcelState.delivering;
        }

        let pos = this._plChar.getPackagePos(this._plCharPkgId);

        if (progress < 0.5) {
            pos.y = pc.math.lerp(this._tmpPos1.y, this._tmpPos1.y + 0.5, pc.CubicOut(progress / 0.5));
        }
        else {
            pos.y = pc.math.lerp(this._tmpPos1.y + 0.5, pos.y, pc.CubicIn((progress - 0.5) / 0.5));
        }

        this._entity.setPosition(
            pc.math.lerp(this._tmpPos1.x, pos.x, progress),
            pos.y,
            pc.math.lerp(this._tmpPos1.z, pos.z, progress)
        );
        this._entity.setEulerAngles(0, 90 * progress, 0);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private updateDelivering(): void {
        this._entity.setPosition(this._plChar.getPackagePos(this._plCharPkgId));
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private updateDelivered(): void {
        this.reset();
        return;

        /*        let progress = (TIMER.time - this._time) / 1000;
        
                if (progress >= 1) {
                    this.reset();
                    return;
                }
        
                this._entity.setPosition(
                    pc.math.lerp(this._tmpPos1.x, this._tmpPos2.x, progress),
                    pc.math.lerp(this._tmpPos1.y, this._tmpPos2.y, progress),
                    pc.math.lerp(this._tmpPos1.z, this._tmpPos2.z, progress)
                );*/
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private updateDiscard(): void {
        let progress = (TIMER.time - this._time) / 750;

        if (progress >= 1) {
            this.reset();
            return;
        }

        ModelUtils.setOpacity(this._entity.model, 1 - progress);
    }
}